import { ApolloCache, StoreObject } from "@apollo/client";
import { getArchivedSkuFilter } from "components/Products/utils";
import type { ArchivedStatus } from "components/Shared/ArchivedStatusButtonGroup";
import { assertPresence } from "utils/assertions";
import { DocumentType, gql } from "__generated__/gql";

export const tableSkuFragment = gql(/* GraphQL */ `
  fragment ProductsTableSku on Sku {
    id
    name
    isArchived
    internalSkuId
    unitNetWeight
    unitNetWeightUnit
    unitCogs
    unitSrp
    grossPalletWeightLbs
    wholesaleUnitPriceRange {
      start {
        value
      }
      end {
        value
      }
    }
    productLine {
      id
      innerCaseQuantity
      wholesaleCaseQuantity
    }
    skuComponentsList {
      id
      skuComponentType
      upc
      ...LabeledSkuComponentUpc
      grossWeightLbs
      dimensions {
        id
        formatted
        cube
      }
    }
  }
`);

export const tableSkusForProductLineFragment = gql(/* GraphQL */ `
  fragment ProductsTableSkusForProductLine on ProductLine {
    id
    skusList(filter: $skuFilter, orderBy: CREATED_AT_ASC) {
      id
      ...ProductsTableSku
    }
  }
`);

export const productLinesSkusTableFieldsFragment = gql(/* GraphQL */ `
  fragment ProductLinesSkusTableFieldsFragment on ProductLine {
    id
    name
    isArchived
  }
`);

export const productLinesSkusTableQuery = gql(/* GraphQL */ `
  query ProductsTable(
    $brandId: UUID!
    $productLineFilter: ProductLineFilter
    $skuFilter: SkuFilter
  ) {
    productLinesList(
      condition: { brandId: $brandId }
      filter: $productLineFilter
      orderBy: CREATED_AT_ASC
    ) {
      ...ProductLinesSkusTableFieldsFragment
      ...ProductsTableSkusForProductLine
    }
  }
`);

export type ProductsTableData = DocumentType<typeof productLinesSkusTableQuery>;

type ProductsTableSkus = DocumentType<
  typeof tableSkusForProductLineFragment
>["skusList"];

export function updateCachedProductLineSkus(
  cache: ApolloCache<unknown>,
  productLine: StoreObject,
  statuses: readonly ArchivedStatus[],
  getSkus: (
    existing: ProductsTableSkus | null | undefined,
  ) => ProductsTableSkus | null | undefined,
) {
  const plCacheId = cache.identify(productLine);

  assertPresence(plCacheId);

  statuses.forEach((status) => {
    const skuFilter = getArchivedSkuFilter(status);

    const cachedData = cache.readFragment({
      fragment: tableSkusForProductLineFragment,
      fragmentName: "ProductsTableSkusForProductLine",
      variables: { skuFilter },
      id: plCacheId,
    });

    const skusList = getSkus(cachedData?.skusList);
    if (!skusList) return;

    cache.writeFragment({
      id: plCacheId,
      fragment: tableSkusForProductLineFragment,
      fragmentName: "ProductsTableSkusForProductLine",
      variables: { skuFilter },
      data: {
        id: productLine.id,
        ...cachedData,
        skusList,
      },
    });
  });
}
